import { useState } from 'react'
import { Button } from '../components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { useOrganizations } from '../hooks/useOrganizations'
import { AuthShell, NoticeBanner } from '../components/Auth'
import { ArchigmaMark } from '../components/Logo'
import { useAuth } from '../lib/auth'
import { completeLoreSession } from '../lib/endpoints/auth'
import { loreOrganizationToAuthorize } from '../lib/loreAuth'

function sessionParams(): { sessionCode: string; clientState: string } | null {
  if (typeof window === 'undefined') return null
  const search = new URLSearchParams(window.location.search)
  const sessionCode = search.get('session_code')
  const clientState = search.get('client_state')
  return sessionCode && clientState ? { sessionCode, clientState } : null
}

export function LoreAuthorizePage() {
  const auth = useAuth()
  const params = sessionParams()
  const [status, setStatus] = useState<'idle' | 'working' | 'done'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('')
  const needsOrganization =
    auth.status === 'authenticated' && !auth.user.active_org
  const organizations = useOrganizations(needsOrganization)

  const organizationRows = organizations.query.data ?? []
  const organizationId =
    auth.status === 'authenticated'
      ? loreOrganizationToAuthorize(
          auth.user.active_org?.id ?? null,
          selectedOrganizationId,
          organizationRows,
        )
      : null
  const organizationReady =
    !needsOrganization ||
    (organizations.query.isSuccess && organizationId !== null)

  const login = () => {
    const returnTo = `${window.location.pathname}${window.location.search}`
    window.location.assign(`/login?return_to=${encodeURIComponent(returnTo)}`)
  }

  const authorize = async () => {
    if (!params || auth.status !== 'authenticated' || !organizationId) return
    setStatus('working')
    setError(null)
    try {
      await completeLoreSession(
        params.sessionCode,
        params.clientState,
        organizationId,
      )
      setStatus('done')
    } catch (cause) {
      setStatus('idle')
      setError(
        cause instanceof Error
          ? cause.message
          : 'Nie udalo sie autoryzowac LORE',
      )
    }
  }

  return (
    <AuthShell>
      <div className="space-y-5 text-center" data-testid="lore-authorize">
        <h1 className="flex items-center justify-center gap-2 text-2xl font-bold text-foreground">
          <ArchigmaMark className="h-7 w-5 text-foreground" />
          Dostep do LORE
        </h1>

        {!params && (
          <NoticeBanner
            tone="error"
            message="Nieprawidlowa lub niepelna sesja LORE."
          />
        )}
        {error && <NoticeBanner tone="error" message={error} />}
        {status === 'done' && (
          <NoticeBanner
            tone="success"
            message="LORE zostal autoryzowany. Mozesz zamknac to okno i wrocic do terminala."
          />
        )}

        {params && status !== 'done' && auth.status === 'unauthenticated' && (
          <>
            <p className="text-sm text-muted-foreground">
              Zaloguj sie kontem Archigma, aby powiazac je z klientem LORE.
            </p>
            <Button className="w-full" size="lg" onClick={login}>
              Zaloguj sie
            </Button>
          </>
        )}

        {params && status !== 'done' && auth.status === 'authenticated' && (
          <>
            <p className="text-sm text-muted-foreground">
              Autoryzujesz klienta LORE jako <strong>{auth.user.email}</strong>.
            </p>
            {needsOrganization && organizations.query.isPending && (
              <p className="text-sm text-muted-foreground">
                Sprawdzanie organizacji...
              </p>
            )}
            {needsOrganization && organizations.query.isError && (
              <NoticeBanner
                tone="error"
                message="Nie udalo sie pobrac organizacji. Odswiez strone i sprobuj ponownie."
              />
            )}
            {needsOrganization &&
              organizations.query.isSuccess &&
              organizationRows.length === 0 && (
                <NoticeBanner
                  tone="error"
                  message="To konto nie nalezy do zadnej organizacji. Popros administratora o nadanie dostepu."
                />
              )}
            {needsOrganization && organizationRows.length === 1 && (
              <p className="text-sm text-muted-foreground">
                Organizacja: <strong>{organizationRows[0].name}</strong>
              </p>
            )}
            {needsOrganization && organizationRows.length > 1 && (
              <Select
                value={selectedOrganizationId || null}
                onValueChange={(value) =>
                  setSelectedOrganizationId(value ?? '')
                }
              >
                <SelectTrigger className="w-full" aria-label="Organizacja LORE">
                  <SelectValue placeholder="Wybierz organizacje" />
                </SelectTrigger>
                <SelectContent>
                  {organizationRows.map((organization) => (
                    <SelectItem key={organization.id} value={organization.id}>
                      {organization.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              className="w-full"
              size="lg"
              disabled={status === 'working' || !organizationReady}
              onClick={() => void authorize()}
            >
              {status === 'working'
                ? 'Autoryzowanie...'
                : needsOrganization &&
                    organizationRows.length > 1 &&
                    !organizationId
                  ? 'Wybierz organizacje'
                  : 'Autoryzuj LORE'}
            </Button>
          </>
        )}

        {auth.status === 'loading' && (
          <p className="text-sm text-muted-foreground">Sprawdzanie sesji...</p>
        )}
        {auth.status === 'apiUnavailable' && (
          <NoticeBanner
            tone="error"
            message="Serwis logowania jest chwilowo niedostepny."
          />
        )}
      </div>
    </AuthShell>
  )
}
